import { apiV2 } from './api';
import { formatDate } from './dateFormat';

/* ─── Matching PB ↔ BK — v2-Endpoint (Score + Feld-Breakdown) ────────────── */

export interface MatchField {
  name: string;
  type: string;
  /** 0…1 — Erfüllungsgrad des Einzelfelds. */
  score: number;
  /**
   * Wirksames Gewicht aus den Admin-Einstellungen → Matching
   * (0 = ignoriert, 1 = Standard, bis 10). Fehlt bei älteren Antworten.
   */
  weight?: number;
  /** true = PB hat zu diesem Feld keinen Wunsch — zählt nicht im Gesamtscore. */
  neutral?: boolean;
  /** Klartext-Werte beider Seiten (nur Detail-Felder, z.B. bk_wohnen). */
  pb?: string[];
  bk?: string[];
}

export interface MatchCandidate {
  id: number;
  name: string;
  user_role: number;
  ort: string | null;
  pflegegrad: number | string | null;
  geburtsdatum: string | null;
  email: string | null;
  /** „Verfügbar ab" (users.startdatum, YYYY-MM-DD) — null = sofort. */
  startdatum?: string | null;
  /** 0…100 */
  score: number;
  /** true = Kandidat hat kein Profil — Score 0, nicht aussagekräftig. */
  incomplete: boolean;
  fields: MatchField[];
  /** Pflegeparameter-Icon-Keys (CareParamIcons) — serverseitig abgeleitet. */
  icons?: string[];
}

export interface MatchingResult {
  anchor: { id: number; user_role: number; incomplete: boolean };
  candidates: MatchCandidate[];
}

/** Kachel im Matching-Board (PB der letzten Stage bzw. verfügbare BK). */
export interface MatchingBoardUser {
  id: number;
  name: string;
  ort: string | null;
  plz: string | null;
  pflegegrad: number | string | null;
  geburtsdatum: string | null;
  assigned_to: number | null;
  /**
   * users.startdatum (YYYY-MM-DD). BK: „Verfügbar ab" (null = sofort);
   * PB: gewünschter Betreuungsbeginn, steht auf der PB-Kachel.
   */
  startdatum?: string | null;
  /** Kachel-Zusatzinfos (Kacheln-PDF). */
  gewicht: number | string | null;
  qualifikation: string | null;
  /** bk_deutsch-Options-ID (119=gut/7-10, 120=ordentlich/4-6, 121=Grund/1-3). */
  bk_deutsch: number | string | null;
  /** BK: Deutsch-Level 1–10 (users.german_skills). */
  german_skills: number | string | null;
  family_account_id: number | null;
  profile_id: number | null;
  /** Nur PB: hatte bereits eine Betreuung (Bestandskunde) — sonst Neukunde. */
  bestandskunde?: boolean;
  /** Wohnraum (Klartext): PB = angeboten, BK = gewünscht/akzeptiert. */
  wohnraum?: string[];
  icons: string[];
  /** Rote Wunsch-Icons (nichtraucher | fuehrerschein | familienleben), additiv ab 23.10.2026. */
  wunsch_icons?: string[];
  /** Nur PB: Keys für die Hervorhebung der BK-Icons (Geschlecht = Wunsch bk_anrede), ab 29.10.2026. */
  match_icons?: string[] | null;
}

export interface MatchingBoard {
  last_phase: { id: number; title: string } | null;
  pbs: MatchingBoardUser[];
  bks: MatchingBoardUser[];
}

export const v2Matching = {
  /** Zwei-Spalten-Board: PBs der letzten Stage + verfügbare BKs (mit Icons). */
  board: async (): Promise<MatchingBoard> => {
    const res = await apiV2.get<{ data: MatchingBoard }>('/admin/matching/board');
    return res.data.data;
  },

  /** Kandidaten der Gegenrolle für einen Anker-User, sortiert nach Score. */
  candidatesFor: async (userId: number, limit = 25): Promise<MatchingResult> => {
    const res = await apiV2.get<{ data: MatchingResult }>(`/admin/matching/${userId}`, {
      params: { limit },
    });
    return res.data.data;
  },
};

/* ─── Startdatum („Verfügbar ab") ────────────────────────────────────────── */

function todayIso(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** YYYY-MM-DD des Startdatums, oder null wenn leer/ungültig. */
function isoDay(v: string | null | undefined): string | null {
  if (!v) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v));
  return m ? m[1] : null;
}

/** Sortier-Schlüssel: leer oder vergangen = heute (sofort verfügbar). */
export function availabilityKey(startdatum: string | null | undefined, now: Date = new Date()): string {
  const today = todayIso(now);
  const d = isoDay(startdatum);
  return d === null || d < today ? today : d;
}

/** true = ab heute verfügbar (Startdatum leer oder nicht in der Zukunft). */
export function isAvailableNow(startdatum: string | null | undefined, now: Date = new Date()): boolean {
  return availabilityKey(startdatum, now) === todayIso(now);
}

/** Anzeige: DD.MM.YYYY — oder null, wenn sofort verfügbar (Aufrufer zeigt „sofort"). */
export function formatStartdatum(startdatum: string | null | undefined, now: Date = new Date()): string | null {
  if (isAvailableNow(startdatum, now)) return null;
  return formatDate(availabilityKey(startdatum, now));
}

/**
 * BKs nach Startdatum sortieren: früheste Verfügbarkeit zuerst (leer oder
 * vergangen = sofort), bei gleichem Tag höherer Score zuerst, dann ID.
 * Gibt eine neue Liste zurück (stabil, Eingabe unverändert).
 */
export function sortByAvailability<T extends { id: number; startdatum?: string | null; score?: number | null }>(
  rows: T[],
  now: Date = new Date(),
): T[] {
  return [...rows].sort((a, b) => {
    const ka = availabilityKey(a.startdatum, now);
    const kb = availabilityKey(b.startdatum, now);
    if (ka !== kb) return ka < kb ? -1 : 1;
    const sa = a.score ?? -1;
    const sb = b.score ?? -1;
    if (sa !== sb) return sb - sa;
    return a.id - b.id;
  });
}

/** Deutsche Labels für die Breakdown-Anzeige (Fallback, falls i18n-Key fehlt). */
export const MATCH_FIELD_LABELS: Record<string, string> = {
  pflegegrad: 'Pflegegrad',
  beweglichkeit: 'Beweglichkeit',
  einschraenkungen: 'Einschränkungen',
  sinneswahrnehmung: 'Sinneswahrnehmung',
  geisteszustand: 'Geisteszustand',
  schlafverhalten: 'Schlafverhalten',
  essen: 'Essen',
  grundpflege: 'Grundpflege',
  hygiene: 'Hygiene',
  urinkontrolle: 'Urinkontrolle',
  stuhlkontrolle: 'Stuhlkontrolle',
  ankleiden: 'Ankleiden',
  wohnsituation: 'Wohnsituation',
  wohnzustand: 'Wohnzustand',
  wohnort: 'Wohnort',
  haustiere: 'Haustiere',
  einkaufsmoeglichkeiten: 'Einkaufsmöglichkeiten',
  charaktereigenschaften: 'Charaktereigenschaften',
  bk_wohnen: 'BK Wohnraum',
  bk_aktivitaeten: 'BK Aktivitäten',
  bk_anrede: 'Bevorzugtes Geschlecht',
  bk_deutsch: 'BK Deutschkenntnisse',
  bk_fuehrerschein: 'BK Führerschein',
  bk_raucher: 'Raucher:in erlaubt',
  ausgepraegtes_familienleben: 'Ausgeprägtes Familienleben',
  sgb5: 'SGB V (Behandlungspflege)',
  raucherhaushalt: 'Raucherhaushalt',
};

/** i18n key per match field — used by MatchDetailsPopover so the breakdown
 * labels render in the active UI language (see i18n hardcoded-strings rollout). */
export const MATCH_FIELD_LABEL_KEYS: Record<string, string> = {
  pflegegrad: 'matching.breakdown.fields.pflegegrad',
  beweglichkeit: 'matching.breakdown.fields.beweglichkeit',
  einschraenkungen: 'matching.breakdown.fields.einschraenkungen',
  sinneswahrnehmung: 'matching.breakdown.fields.sinneswahrnehmung',
  geisteszustand: 'matching.breakdown.fields.geisteszustand',
  schlafverhalten: 'matching.breakdown.fields.schlafverhalten',
  essen: 'matching.breakdown.fields.essen',
  grundpflege: 'matching.breakdown.fields.grundpflege',
  hygiene: 'matching.breakdown.fields.hygiene',
  urinkontrolle: 'matching.breakdown.fields.urinkontrolle',
  stuhlkontrolle: 'matching.breakdown.fields.stuhlkontrolle',
  ankleiden: 'matching.breakdown.fields.ankleiden',
  wohnsituation: 'matching.breakdown.fields.wohnsituation',
  wohnzustand: 'matching.breakdown.fields.wohnzustand',
  wohnort: 'matching.breakdown.fields.wohnort',
  haustiere: 'matching.breakdown.fields.haustiere',
  einkaufsmoeglichkeiten: 'matching.breakdown.fields.einkaufsmoeglichkeiten',
  charaktereigenschaften: 'matching.breakdown.fields.charaktereigenschaften',
  bk_wohnen: 'matching.breakdown.fields.bk_wohnen',
  bk_aktivitaeten: 'matching.breakdown.fields.bk_aktivitaeten',
  bk_anrede: 'matching.breakdown.fields.bk_anrede',
  bk_deutsch: 'matching.breakdown.fields.bk_deutsch',
  bk_fuehrerschein: 'matching.breakdown.fields.bk_fuehrerschein',
  bk_raucher: 'matching.breakdown.fields.bk_raucher',
  ausgepraegtes_familienleben: 'matching.breakdown.fields.ausgepraegtes_familienleben',
  sgb5: 'matching.breakdown.fields.sgb5',
  raucherhaushalt: 'matching.breakdown.fields.raucherhaushalt',
};

/** Translated label for a match-breakdown field (falls back to the German map, then the raw field name). */
export function matchFieldLabel(name: string, tr: (key: string) => string): string {
  const key = MATCH_FIELD_LABEL_KEYS[name];
  if (!key) return MATCH_FIELD_LABELS[name] ?? name;
  return tr(key) || MATCH_FIELD_LABELS[name] || name;
}
